import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { landingAssetsForLocale } from '../config/landingAssets'

interface Feature {
  imgSrc: string
  pillText: string
  title: ReactNode
  description: ReactNode
}

function HeroTitle() {
  const { t } = useTranslation('landingPage')

  return (
    <h1 className='text-gray-12 whitespace-pre-line text-2xl font-semibold'>
      {t('hero.postPurchaseTitle')}
    </h1>
  )
}

function FeatureItem({ feature }: { feature: Feature }) {
  return (
    <div className='w-full max-w-md'>
      <img
        src={feature.imgSrc}
        alt={feature.pillText}
        className='mb-6 w-full rounded-lg'
        loading='lazy'
      />
      <div className='mx-auto mb-2 block w-fit'>
        <span className='rounded-full bg-[#F29CB2] px-2 py-1 text-sm font-bold uppercase text-white'>
          {feature.pillText}
        </span>
      </div>
      <div className='space-y-4 text-center'>
        <h2 className='text-gray-12 text-xl font-bold'>{feature.title}</h2>
        <p className='localized-body-copy text-gray-12 tracking-0 text-base font-normal leading-[160%]'>
          {feature.description}
        </p>
      </div>
    </div>
  )
}

export function HeroSectionForPostPurchaseModal() {
  const { t, i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const features = [
    {
      imgSrc: assets.featureImages.postPurchaseModal[0],
      pillText: 'Point 1',
      title: <span className='whitespace-pre-line'>{t('hero.features.0.title')}</span>,
      description: <span className='whitespace-pre-line'>{t('hero.features.0.description')}</span>,
    },
    {
      imgSrc: assets.featureImages.postPurchaseModal[1],
      pillText: 'Point 2',
      title: <span className='whitespace-pre-line'>{t('hero.features.1.title')}</span>,
      description: <span className='whitespace-pre-line'>{t('hero.features.1.description')}</span>,
    },
    {
      imgSrc: assets.featureImages.postPurchaseModal[2],
      pillText: 'Point 3',
      title: t('hero.postPurchaseFeature.title'),
      description: (
        <span className='whitespace-pre-line'>{t('hero.postPurchaseFeature.description')}</span>
      ),
    },
  ]

  return (
    <section className='px-4 pb-8 pt-10'>
      <div className='mb-8 text-center'>
        <HeroTitle />
      </div>

      <div className='flex flex-col items-center justify-center space-y-10'>
        {features.map(feature => (
          <FeatureItem key={feature.pillText} feature={feature} />
        ))}
      </div>
    </section>
  )
}

export default function HeroSection() {
  const { t, i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const features = [
    {
      imgSrc: assets.featureImages.default[0],
      pillText: 'Point 1',
      title: <span className='whitespace-pre-line'>{t('hero.features.0.title')}</span>,
      description: t('hero.features.0.description'),
    },
    {
      imgSrc: assets.featureImages.default[1],
      pillText: 'Point 2',
      title: <span className='whitespace-pre-line'>{t('hero.features.1.title')}</span>,
      description: <span className='whitespace-pre-line'>{t('hero.features.1.description')}</span>,
    },
    {
      imgSrc: assets.featureImages.default[2],
      pillText: 'Point 3',
      title: t('hero.features.2.title'),
      description: t('hero.features.2.description'),
    },
  ]
  return (
    <section className='px-4 pb-8 pt-10'>
      <div className='mb-8 text-center'>
        <h2 className='text-gray-12 text-2xl font-semibold'>{t('hero.heading')}</h2>
      </div>

      <div className='flex flex-col items-center justify-center space-y-10'>
        {features.map(feature => (
          <FeatureItem key={feature.pillText} feature={feature} />
        ))}
      </div>
    </section>
  )
}
